// MAPS: searchable, filterable grid of map cards (the server catalog and the
// maps on this device) with a details drawer.

import { useState } from 'preact/hooks';
import { Icon } from '../components/Icon';
import { MapCard } from '../components/MapCard';
import { filterMaps, type MapFilter, type MapSort } from '../maps/catalog';
import { useMapEntries } from '../maps/useCatalog';
import { net, selectedMap, settings, updateSettings } from '../state';
import { MapDetails } from './MapDetails';

const FILTERS: { id: MapFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'favorites', label: 'Favorites' },
  { id: 'recent', label: 'Recent' },
  { id: 'timerun', label: 'With timerun' },
  { id: 'local', label: 'Downloaded' },
];

const SORTS: { id: MapSort; label: string }[] = [
  { id: 'name', label: 'Name' },
  { id: 'recent', label: 'Recently played' },
  { id: 'difficulty', label: 'Difficulty' },
];

export function toggleFavorite(name: string): void {
  const favs = settings.peek().favorites;
  updateSettings({ favorites: favs.includes(name) ? favs.filter((f) => f !== name) : [...favs, name] });
}

export function MapsScreen() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<MapFilter>('all');
  const [sort, setSort] = useState<MapSort>('name');
  const [tag, setTag] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<number | null>(null);
  const s = settings.value;
  const entries = useMapEntries();
  const shown = filterMaps(entries, { query, filter, sort, tag, difficulty });
  // the tags the server's maps have
  const tags = [...new Set(entries.flatMap((e) => e.info?.tags ?? []))].sort();
  const difficulties = [...new Set(entries.map((e) => e.info?.difficulty).filter((d): d is number => !!d))].sort();
  const selected = selectedMap.value;
  const offline = net.value.status !== 'online';

  return (
    <div class={`screen maps-screen${selected ? ' with-drawer' : ''}`}>
      <div class="maps-main">
        <div class="filter-bar">
          <label class="search">
            <Icon name="search" size={16} />
            <input type="search" class="input" placeholder="Search maps…" value={query} aria-label="Search maps" spellcheck={false} onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)} />
          </label>
          <div class="chips" role="radiogroup" aria-label="Filter">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" role="radio" aria-checked={filter === f.id} class={`chip${filter === f.id ? ' active' : ''}`} onClick={() => setFilter(f.id)}>
                {f.label}
                {f.id === 'favorites' && s.favorites.length > 0 && <span class="chip-count">{s.favorites.length}</span>}
              </button>
            ))}
          </div>
          <label class="sort">
            <span class="muted">Sort</span>
            <select class="input" value={sort} onChange={(e) => setSort((e.currentTarget as HTMLSelectElement).value as MapSort)}>
              {SORTS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {(tags.length > 0 || difficulties.length > 0) && (
          <div class="filter-bar filter-bar-tags">
            {tags.length > 0 && (
              <div class="chips" role="radiogroup" aria-label="Tags">
                {tags.map((t) => (
                  <button key={t} type="button" role="radio" aria-checked={tag === t} class={`chip small${tag === t ? ' active' : ''}`} onClick={() => setTag(tag === t ? null : t)}>
                    {t}
                  </button>
                ))}
              </div>
            )}
            {difficulties.length > 0 && (
              <label class="sort">
                <span class="muted">Difficulty</span>
                <select class="input" value={difficulty ?? ''} onChange={(e) => setDifficulty(Number((e.currentTarget as HTMLSelectElement).value) || null)}>
                  <option value="">Any</option>
                  {difficulties.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        <div class="result-count muted">
          {shown.length} of {entries.length} maps
          {offline && ' · offline: only maps on this device'}
        </div>
        {shown.length ? (
          <div class="map-grid">
            {shown.map((e) => (
              <MapCard key={e.name} entry={e} selected={selected === e.name} onSelect={() => (selectedMap.value = selected === e.name ? null : e.name)} onToggleFavorite={() => toggleFavorite(e.name)} />
            ))}
          </div>
        ) : (
          <div class="empty-state">
            <Icon name="search" size={28} />
            <p>{entries.length ? 'No maps match.' : 'No maps yet: connect to the server.'}</p>
          </div>
        )}
      </div>
      {selected && <MapDetails name={selected} onClose={() => (selectedMap.value = null)} />}
    </div>
  );
}
